import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { listServices, listPackages, listPortfolioFeatured } from "../lib/api";
import { SkeletonCard } from "../components/shared/Skeleton";
import { openLightbox } from "../lib/lightbox-store";
import { t } from "../lib/i18n";

export default function Search() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initial = params.get("q") || "";
  const [query, setQuery] = useState(initial);
  const [data, setData] = useState({ services: [], packages: [], portfolio: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => setQuery(initial), [initial]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [services, packages, portfolio] = await Promise.all([
          listServices(),
          listPackages({ limit: 50 }),
          listPortfolioFeatured({ limit: 60 }),
        ]);
        if (!cancelled) setData({ services, packages, portfolio });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    const m = (s) => typeof s === "string" && s.toLowerCase().includes(q);
    return {
      services: data.services.filter(
        (s) => m(s.name) || m(s.tagline) || m(s.description) || (s.occasions || []).some(m),
      ),
      packages: data.packages.filter((p) => m(p.name) || m(p.summary)),
      portfolio: data.portfolio.filter((i) => m(i.title) || (i.tags || []).some(m)),
    };
  }, [query, data]);

  return (
    <section
      data-testid="search-page"
      className="mx-auto max-w-3xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28"
    >
      <p className="text-xs uppercase tracking-[0.32em] text-[#C8A24B]">{t("nav.search")}</p>
      <h1 className="mt-3 font-display text-4xl text-[#1A1A1A] sm:text-5xl">
        {query ? `Results for “${query}”` : t("search.heading")}
      </h1>

      <div className="mt-8">
        <input
          type="text"
          data-testid="search-page-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") navigate(`/search?q=${encodeURIComponent(query.trim())}`);
          }}
          placeholder={t("search.placeholder")}
          className="w-full rounded-full border border-[#EAE3D2] bg-white px-5 py-3 text-sm text-[#1A1A1A] placeholder-[#1A1A1A]/40 outline-none focus:border-[#C8A24B]"
        />
      </div>

      <div className="mt-10">
        {loading ? (
          <ul className="space-y-4">
            {[0, 1].map((i) => (
              <li key={i}>
                <SkeletonCard />
              </li>
            ))}
          </ul>
        ) : !results ? (
          <p className="text-sm text-[#1A1A1A]/55">{t("search.placeholder")}</p>
        ) : results.services.length + results.packages.length + results.portfolio.length === 0 ? (
          <div data-testid="search-page-empty" className="rounded-lg border border-dashed border-[#EAE3D2] bg-white/60 px-6 py-12 text-center">
            <p className="font-display text-2xl text-[#1A1A1A]">{t("search.emptyTitle")}</p>
            <p className="mt-3 text-sm text-[#1A1A1A]/60">{t("search.emptySubtitle")}</p>
            <Link
              to="/plan"
              data-testid="search-page-cta"
              className="mt-7 inline-flex items-center rounded-full border border-[#1A1A1A] px-6 py-2.5 text-xs uppercase tracking-[0.24em] text-[#1A1A1A] transition hover:bg-[#1A1A1A] hover:text-[#FAF7F2]"
            >
              {t("search.emptyCta")}
            </Link>
          </div>
        ) : (
          <div className="space-y-10">
            {results.services.length ? (
              <div data-testid="search-page-services">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/50">
                  {t("search.groups.services")}
                </p>
                <ul className="mt-3 divide-y divide-[#EAE3D2]">
                  {results.services.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/services/${s.slug}`}
                        className="block py-3 text-[#1A1A1A] transition hover:text-[#C8A24B]"
                      >
                        <span className="font-display text-lg">{s.name}</span>
                        {s.tagline ? (
                          <span className="ml-3 text-xs text-[#1A1A1A]/60">{s.tagline}</span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {results.packages.length ? (
              <div data-testid="search-page-packages">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/50">
                  {t("search.groups.packages")}
                </p>
                <ul className="mt-3 divide-y divide-[#EAE3D2]">
                  {results.packages.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/packages/${p.id}`}
                        className="block py-3 text-[#1A1A1A] transition hover:text-[#C8A24B]"
                      >
                        <span className="font-display text-lg">{p.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {results.portfolio.length ? (
              <div data-testid="search-page-portfolio">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#1A1A1A]/50">
                  {t("search.groups.portfolio")}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5">
                  {results.portfolio.slice(0, 15).map((i, idx) => (
                    <li key={i.id}>
                      <button
                        type="button"
                        onClick={() => openLightbox(results.portfolio, idx)}
                        className="block aspect-square w-full overflow-hidden rounded-md bg-[#EAE3D2]"
                      >
                        <img
                          src={i.thumbnail_url || i.url}
                          alt={i.title || "Search result"}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
